/**
 * The main component that renders single TabComponent
 * instances.
 */

import {
	Component,
	ContentChildren,
	ElementRef,
	EventEmitter,
	Input,
	Output,
	QueryList,
	AfterContentInit,
	ViewChildren,
} from "@angular/core";

import { TabComponent } from "../tab-component/tab.component";

@Component({
    selector: "my-tabs",
    templateUrl: "./tabs.component.html",
    styleUrls: ["./tabs.component.scss"],
    standalone: false
})
export class TabsComponent implements AfterContentInit {
	@ContentChildren(TabComponent) tabs: QueryList<TabComponent>;
	@ViewChildren("tabTrigger") tabTriggers: QueryList<ElementRef<HTMLAnchorElement>>;

	/**
	 * Optional localStorage key. When set, the last selected tab title
	 * is persisted under this key so the tab survives a re-mount (e.g.
	 * Manage > Series, where doing an action on a non-Video tab used to
	 * snap the user back to Video Series after the sub-route returned).
	 */
	@Input() storageKey?: string;

	/** Emits the active tab's index whenever the active tab changes. */
	@Output() tabChange = new EventEmitter<number>();

	ngAfterContentInit() {
		let activeTabs = this.tabs.filter((tab) => tab.active);

		if (activeTabs.length === 0) {
			const remembered = this.readRemembered();
			const match = remembered
				? this.tabs.find((tab) => tab.title === remembered)
				: null;
			this.selectTab(match ?? this.tabs.first);
		}
	}

	selectTab(tab: TabComponent) {
		if (!tab) {
			return;
		}
		const tabArr = this.tabs.toArray();
		tabArr.forEach((t) => (t.active = false));
		tab.active = true;
		this.writeRemembered(tab.title);
		this.tabChange.emit(tabArr.indexOf(tab));
	}

	onTabKeydown(event: KeyboardEvent, index: number) {
		const tabArr = this.tabs.toArray();
		if (!tabArr.length) {
			return;
		}

		switch (event.key) {
			case "ArrowRight":
			case "ArrowDown":
				event.preventDefault();
				this.moveFocus(index, 1);
				break;
			case "ArrowLeft":
			case "ArrowUp":
				event.preventDefault();
				this.moveFocus(index, -1);
				break;
			case "Home":
				event.preventDefault();
				this.moveFocus(index, -index, true);
				break;
			case "End":
				event.preventDefault();
				this.moveFocus(index, tabArr.length - 1 - index, true);
				break;
			case "Enter":
			case " ":
				event.preventDefault();
				this.selectTab(tabArr[index]);
				break;
			default:
				return;
		}
	}

	private moveFocus(currentIndex: number, step: number, select = false) {
		const tabArr = this.tabs.toArray();
		if (!tabArr.length) {
			return;
		}

		const nextIndex = (currentIndex + step + tabArr.length) % tabArr.length;
		const nextTab = tabArr[nextIndex];
		if (select) {
			this.selectTab(nextTab);
		}
		this.tabTriggers?.toArray()[nextIndex]?.nativeElement.focus();
	}

	private readRemembered(): string | null {
		if (!this.storageKey) {
			return null;
		}
		try {
			return localStorage.getItem(this.storageKey);
		} catch {
			return null;
		}
	}

	private writeRemembered(title: string): void {
		if (!this.storageKey || !title) {
			return;
		}
		try {
			localStorage.setItem(this.storageKey, title);
		} catch {
			/* ignore: localStorage unavailable / quota exceeded */
		}
	}
}
